import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useModal } from 'components/modals/ModalContext';
import { useAuthContext } from 'services/authentication/AuthenticationContext';
import { checkRequiredFeatureActive } from 'services/permissions/permissionChecks';
import { widgetCatalog } from './dashboardConfig';
import { AdvancedWidgetConfig, DashboardLayoutItem } from 'types/WidgetTypes';
import SwitchButton from 'components/buttons/SwitchButton';
import PrimaryButton from 'components/buttons/PrimaryButton';
import '../../style/scss/dashboard.scss';

/*
 * CustomizeDashboardModal.tsx
 * Catalog of every dashboard widget, grouped per domain, each one
 * switched on or off. Confirming saves the updated widget layout,
 * newly added widgets are stacked below the existing ones on the
 * always draggable and resizable grid.
 */

interface CustomizeDashboardModalProps {
    initialLayout: DashboardLayoutItem[];
    onSave: (layout: DashboardLayoutItem[]) => void;
}

const CustomizeDashboardModal: React.FC<CustomizeDashboardModalProps> = ({ initialLayout, onSave }) => {
    const { t } = useTranslation();
    const { revealModal, closeModal } = useModal();
    const { activeFeatures, roleFeatures, allowedFeatures } = useAuthContext();
    const featureContext = { activeFeatures, roleFeatures, allowedFeatures };

    const [layout, setLayout] = useState<DashboardLayoutItem[]>(initialLayout);

    // Reveal the modal on first render
    useEffect(() => {
        revealModal();
    }, []);

    // Get the domain groups a widget is listed under, falling back to its single domain
    const getWidgetDomains = (widget: AdvancedWidgetConfig) => widget.displayDomains ?? [widget.domain];

    // Group the catalog widgets per domain, in catalog order, only keeping domains with at least one active-feature widget
    const domains = Array.from(new Set(widgetCatalog.flatMap(widget => getWidgetDomains(widget)))).filter(domain =>
        widgetCatalog.some(widget => getWidgetDomains(widget).includes(domain) && checkRequiredFeatureActive(widget.requiredFeature, featureContext))
    );

    // Toggle a widget on or off in the local layout state
    const toggleWidget = (widgetKey: string, isOn: boolean) => {
        if (isOn) {
            const widget = widgetCatalog.find(widget => widget.widgetKey === widgetKey);
            if (!widget) return;

            // Use the functional update form so rapid consecutive toggles each build on
            // the latest state instead of overwriting each other with a stale layout
            setLayout(currentLayout => {
                // Stack the new widget below the lowest widget currently in the layout
                const lowestY = currentLayout.reduce((max, item) => Math.max(max, item.y + item.h), 0);
                return [...currentLayout, {
                    widget_key: widgetKey,
                    x: 0,
                    y: lowestY,
                    w: widget.defaultGridSize.w,
                    h: widget.defaultGridSize.h,
                }];
            });
        } else {
            setLayout(currentLayout => currentLayout.filter(item => item.widget_key !== widgetKey));
        }
    };

    // Save the layout and close the modal
    const handleSave = () => {
        onSave(layout);
        closeModal();
    };

    return (
        <div className='customize-dashboard-modal'>
            <p className='customize-dashboard-intro'>{t('dashboard.advanced_widget.catalog_title')}</p>
            <div className='customize-dashboard-domains-grid'>
                {domains.map(domain => (
                    <div key={domain} className='customize-dashboard-domain'>
                        <h6>{t(`dashboard.advanced_widget.domain.${domain}`)}</h6>
                        {widgetCatalog.filter(widget => getWidgetDomains(widget).includes(domain)).map(widget => {
                            const featureActive = checkRequiredFeatureActive(widget.requiredFeature, featureContext);
                            const isOn = layout.some(item => item.widget_key === widget.widgetKey);

                            return (
                                <div key={widget.widgetKey} className='customize-dashboard-widget-row'>
                                    <span>{t(widget.title)}</span>
                                    <SwitchButton
                                        value={isOn}
                                        isDisabled={!featureActive}
                                        onToggle={(newIsOn) => toggleWidget(widget.widgetKey, newIsOn)}
                                    />
                                </div>
                            );
                        })}
                    </div>
                ))}
            </div>
            <div className='button-row'>
                <PrimaryButton
                    onClick={handleSave}
                    label='dashboard.advanced_widget.customize_done_button'
                    size='small'
                />
            </div>
        </div>
    );
};

export default CustomizeDashboardModal;
